import { apiClient } from '@/lib/api/client'

/** Ticket dashboard (helpdesk-style) types — same shape as actionlabs-support-admin */
export interface TicketSourceItem {
  name: string
  value: number
  color: string
}

export interface TicketSource {
  period: string
  total_active: number
  sources: TicketSourceItem[]
}

export interface AgentPerformanceItem {
  agent_id: number
  name: string
  initials: string
  team: string
  open_ticket: number
  status: string
  avg_frt_seconds: number
  csat: number | null
  load_percent: number
}

export interface HelpdeskDashboardStats {
  unresolved: number
  overdue: number
  due_today: number
  open: number
  on_hold: number
  unassigned: number
  resolved: number
  received: number
  net_recovery_minutes: number
  resolution_within_sla_percent: number
  trends_today: number[]
  trends_yesterday: number[]
  kpi_open: number
  kpi_in_progress: number
  kpi_pending: number
  kpi_on_hold: number
  kpi_closed: number
  kpi_cancelled: number
  ticket_source: TicketSource
  agent_performance: AgentPerformanceItem[]
  sla_breach_total?: number
  sla_breach_by_source?: TicketSourceItem[]
}

export interface DashboardStats {
  total_users: number
  total_media: number
  total_categories: number
  total_tags: number
}

export interface EnhancedDashboardStats extends DashboardStats {
  active_users: number
  new_users_today: number
  new_users_this_week: number
  new_users_this_month: number
  users_last_30_days: number
  total_pages: number
  published_pages: number
  draft_pages: number
  pages_this_month: number
  media_this_month: number
  activities_today: number
  activities_this_week: number
}

export interface DashboardWidget {
  id: number
  user_id: number | null
  widget_type: 'metric' | 'line_chart' | 'bar_chart' | 'pie_chart' | 'table'
  title: string
  data_source: string
  query_config: Record<string, any>
  visualization_config: Record<string, any>
  position_x: number
  position_y: number
  width: number
  height: number
  order_index: number
  active: boolean
  created_at: string
  updated_at: string
}

export interface ChartData {
  labels: string[]
  data: number[]
}

export interface DashboardTicketDistributionRow {
  division: string
  incident: number
  service: number
  request: number
}

export interface DashboardResolutionByTypeSlaRow {
  label: string
  within_sla: number
  nearing_sla: number
  breached: number
}

export interface DashboardTicketResolutionTrend {
  labels: string[]
  current: number[]
  previous: number[]
}

export interface DashboardTicketTrendByWeekTypeRow {
  label: string
  incident: number
  service: number
  request: number
}

export interface DashboardMttrByCategoryRow {
  category: string
  under_4h: number
  from_4h_to_1d: number
  over_1d: number
  service_type_id?: number
}

export interface DashboardItemsPerSubCategoryRow {
  sub_category: string
  service: number
  incident: number
  request: number
}

export interface DashboardChartsData {
  ticketDistribution: DashboardTicketDistributionRow[]
  resolutionByTypeSla: DashboardResolutionByTypeSlaRow[]
  ticketResolutionTrend: DashboardTicketResolutionTrend
  ticketTrendByWeekType: DashboardTicketTrendByWeekTypeRow[]
  mttrByCategory: DashboardMttrByCategoryRow[]
  itemsPerSubCategory: DashboardItemsPerSubCategoryRow[]
}

export interface DashboardChartsParams {
  start_date?: string
  end_date?: string
  range_type?: 'monthly' | 'weekly' | 'custom'
  selected_week?: number
  service_type?: 'all' | 'incident' | 'inquiry' | 'request' | 'service'
  service_type_id?: number | null
  ticket_status_id?: number | null
}

export const dashboardService = {
  /**
   * Get basic dashboard statistics
   */
  getStats: async (): Promise<DashboardStats> => {
    const response = await apiClient.get('/dashboard/stats')
    return response.data.data
  },

  /**
   * Get enhanced dashboard statistics
   */
  getEnhancedStats: async (): Promise<EnhancedDashboardStats> => {
    const response = await apiClient.get('/dashboard/enhanced-stats')
    return response.data.data
  },

  /**
   * Get all dashboard widgets
   */
  getWidgets: async (): Promise<DashboardWidget[]> => {
    const response = await apiClient.get('/dashboard/widgets')
    return response.data.data
  },

  /**
   * Get widget data
   */
  getWidgetData: async (widgetId: number): Promise<any> => {
    const response = await apiClient.get(`/dashboard/widgets/${widgetId}/data`)
    return response.data.data
  },

  /**
   * Get user registration trend
   */
  getUserRegistrationTrend: async (
    period: 'daily' | 'weekly' | 'monthly' = 'daily',
    days: number = 30
  ): Promise<ChartData> => {
    const response = await apiClient.get('/dashboard/user-registration-trend', {
      params: { period, days },
    })
    return response.data.data
  },

  /**
   * Get pages by status
   */
  getPagesByStatus: async (): Promise<ChartData> => {
    const response = await apiClient.get('/dashboard/pages-by-status')
    return response.data.data
  },

  /**
   * Get ticket/support dashboard statistics (helpdesk-style).
   * Optional filters: start_date, end_date (Y-m-d), service_type_id, ticket_status_id.
   */
  getTicketStats: async (params?: {
    start_date?: string
    end_date?: string
    service_type_id?: number | null
    ticket_status_id?: number | null
  }): Promise<HelpdeskDashboardStats> => {
    const searchParams = new URLSearchParams()
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.service_type_id != null && params.service_type_id > 0) {
      searchParams.set('service_type_id', String(params.service_type_id))
    }
    if (params?.ticket_status_id != null && params.ticket_status_id > 0) {
      searchParams.set('ticket_status_id', String(params.ticket_status_id))
    }
    const query = searchParams.toString()
    const url = query ? `/dashboard/ticket-stats?${query}` : '/dashboard/ticket-stats'
    const response = await apiClient.get(url)
    return response.data.data
  },

  /**
   * Get all dashboard chart datasets with backend-driven grouping.
   */
  getDashboardCharts: async (params?: DashboardChartsParams): Promise<DashboardChartsData> => {
    const searchParams = new URLSearchParams()
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.range_type) searchParams.set('range_type', params.range_type)
    if (params?.selected_week != null && params.selected_week > 0) {
      searchParams.set('selected_week', String(params.selected_week))
    }
    if (params?.service_type) searchParams.set('service_type', params.service_type)
    if (params?.service_type_id != null && params.service_type_id > 0) {
      searchParams.set('service_type_id', String(params.service_type_id))
    }
    if (params?.ticket_status_id != null && params.ticket_status_id > 0) {
      searchParams.set('ticket_status_id', String(params.ticket_status_id))
    }

    const query = searchParams.toString()
    const url = query ? `/dashboard/charts?${query}` : '/dashboard/charts'
    const response = await apiClient.get(url)
    return response.data.data
  },

  /**
   * Requestor dashboard: stats for current user's ticket requests
   */
  getRequestorStats: async (): Promise<{
    total_requests: number
    in_progress: number
    open_requests: number
    resolved_requests: number
  }> => {
    const response = await apiClient.get('/dashboard/requestor/stats')
    return response.data.data
  },

  /**
   * Requestor dashboard: latest 10 ticket requests for current user
   */
  getRequestorLatestRequests: async (): Promise<{ data: any[] }> => {
    try {
      const response = await apiClient.get('/dashboard/requestor/latest-requests')
      const data = response.data?.data ?? []
      return { data: Array.isArray(data) ? data : [] }
    } catch {
      return { data: [] }
    }
  },
}

